/**
 * Slack API client for frontend integration
 */

const API_BASE_URL = import.meta.env.VITE_API_URL
	? `${import.meta.env.VITE_API_URL}/api/v1`
	: '/api/v1';

export interface SlackSendRequest {
	credentialId: string;
	channels: string[];
	format: 'text' | 'markdown' | 'blocks';
	content: {
		text?: string;
		blocks?: any[];
	};
	threading?: {
		mode: 'none' | 'reply' | 'start_new';
		threadTs?: string;
		exposeTs?: boolean;
	};
	options?: {
		unfurlLinks?: boolean;
		unfurlMedia?: boolean;
		linkNames?: boolean;
		metadata?: Record<string, any>;
	};
	outputs?: {
		exposePermalinks?: boolean;
		exposeChannelIds?: boolean;
		exposeChannelNames?: boolean;
	};
	preview?: boolean;
}

export interface SlackPreviewRequest extends SlackSendRequest {
	sampleContext?: {
		data?: Record<string, any>;
	};
	preview: true;
}

export interface SlackSendResponse {
	messages: Array<{
		channel: string;
		channelName?: string;
		ts: string;
		thread_ts?: string;
		permalink?: string;
		success: boolean;
		error?: string;
	}>;
	summary: {
		totalSent: number;
		totalFailed: number;
		channels: string[];
	};
}

export interface SlackPreviewResponse {
	channels: Array<{
		channel: string;
		channelName?: string;
		renderedContent: {
			text?: string;
			blocks?: any[];
			format: string;
		};
		valid: boolean;
		errors?: string[];
		warnings?: string[];
	}>;
	summary: {
		validChannels: number;
		invalidChannels: number;
		tokensResolved: number;
		tokensFound: string[];
	};
	globalErrors?: string[];
	globalWarnings?: string[];
}

export interface SlackChannel {
	id: string;
	name: string;
	is_private: boolean;
	is_member: boolean;
	displayName: string;
	canPost: boolean;
}

export interface SlackChannelsResponse {
	channels: SlackChannel[];
}

class SlackApiClient {
	private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		const token = sessionStorage.getItem('auth_token');

		if (!token) {
			throw new Error('Authentication token not found');
		}

		const url = `${API_BASE_URL}${endpoint}`;

		const response = await fetch(url, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
				...options.headers,
			},
		});

		if (!response.ok) {
			let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

			try {
				const errorData = await response.json();
				if (errorData.message) {
					errorMessage = errorData.message;
				}
			} catch {
				// Use default error message if JSON parsing fails
			}

			throw new Error(errorMessage);
		}

		return response.json();
	}

	/**
	 * Send messages to Slack channels
	 */
	async send(request: SlackSendRequest): Promise<SlackSendResponse> {
		return this.makeRequest<SlackSendResponse>('/integrations/slack/send', {
			method: 'POST',
			body: JSON.stringify(request),
		});
	}

	/**
	 * Generate message preview with sample data
	 */
	async preview(request: SlackPreviewRequest): Promise<SlackPreviewResponse> {
		return this.makeRequest<SlackPreviewResponse>('/integrations/slack/preview', {
			method: 'POST',
			body: JSON.stringify(request),
		});
	}

	/**
	 * Get available Slack channels
	 */
	async getChannels(): Promise<SlackChannelsResponse> {
		return this.makeRequest<SlackChannelsResponse>('/integrations/slack/channels', {
			method: 'POST',
		});
	}
}

export const slackApi = new SlackApiClient();
